import {
  findShowEpisodeByPlayableId,
  DEFAULT_RIGHTS,
} from '../../player/utils/viewable';
import {
  CHROMECAST_STATES,
  getRating,
} from '../Player/utils';
import * as playerEventsActions from '../actions/player-events';
import { SUBTITLES_OFF } from '../../common/constants';

const NO_BUFFER = {
  start: 0,
  end: 0,
};

const NO_SEEK_RANGE = {
  start: 0,
  startAbsolute: 0,
  end: 0,
  endAbsolute: 0,
};

const DEFAULT_DEVICE_NAME = 'Chromecast';

const EMPTY_VIEWABLE_METADATA = {
  title: '',
  banner: null,
  start: undefined,
  stop: undefined,
  episode: null,
  isLiveEvent: false,
};

const getChromecastState = (player) => {
  if (!player.model.isChromeCastAvailable) {
    return CHROMECAST_STATES.UNAVAILABLE;
  }
  return player.model.isChromeCasting
    ? CHROMECAST_STATES.CONNECTED
    : CHROMECAST_STATES.DISCONNECTED;
};

const isNotEmptyString = x => x && x.trim();
const normalizeOptions = options => (options ? options.filter(isNotEmptyString) : []);
const normalizeTextTracksOptions = (rawOptions) => {
  const options = normalizeOptions(rawOptions);

  return [
    SUBTITLES_OFF,
    ...options,
  ];
};

export const defaultState = {
  currentTime: 0,
  currentTimeAd: 0,
  rights: DEFAULT_RIGHTS,
  playing: false,
  isInitialized: false,
  isBuffering: true,
  volume: 0,
  buffered: NO_BUFFER,
  seekRange: NO_SEEK_RANGE,

  isLive: false,
  streamDuration: 0,
  offsetFromLive: 0,

  audioTracks: [],
  textTracks: [],
  videoTracks: [],
  audioTrack: undefined,
  subtitleTrack: undefined,
  bitrate: undefined,

  isPlayingAds: false,
  isPlayingDaiAds: false,
  adsMetadata: null,
  adTitle: null,
  adsCount: 0,
  adsLeft: 0,
  adSkipCountdown: -1,

  chromecastState: CHROMECAST_STATES.UNAVAILABLE,
  chromecastFriendlyName: DEFAULT_DEVICE_NAME,

  isAirPlayAvailable: false,
  isAirPlaying: false,

  isFullscreen: false,

  error: null,
  isPinCodeRequired: false,

  viewableMetadata: EMPTY_VIEWABLE_METADATA,
  metadata: {},

  chromecastEnabled: false,
  airplayEnabled: false,

  nextVideo: false,
};

export default function playback(state = defaultState, action = {}) {
  switch (action.type) {
    case playerEventsActions.PREFLIGHT: {
      const { player } = action;

      return {
        ...state,

        currentTime: player.model.currentTime,
        playing: player.model.isPlaying,
        volume: player.model.volume,
        error: null,
        chromecastState: getChromecastState(player),
        metadata: player.model.metadata,
      };
    }

    case playerEventsActions.VIEWABLE: {
      const {
        viewable,
        rights,
        assetId,
      } = action.player.model;

      if (!viewable) {
        return {
          ...state,

          rights,
          viewableMetadata: EMPTY_VIEWABLE_METADATA,
        };
      }

      const isLiveEvent = viewable.defaultPlayable?.kind === 'LIVE_EVENT' || viewable.playable?.kind === 'LIVE_EVENT';

      if (viewable.__typename === 'Channel') {
        const broadcast = viewable.broadcastById || viewable.schedule?.[0] || {};

        return {
          ...state,

          rights,
          viewableMetadata: {
            title: broadcast.title || viewable.title || '',
            banner: broadcast.banner,
            start: isLiveEvent ? undefined : broadcast.start,
            stop: isLiveEvent ? undefined : broadcast.stop,
            isLiveEvent,
          },
        };
      }

      if (viewable.__typename === 'Show') {
        const episode = findShowEpisodeByPlayableId(viewable, assetId);
        const playable = episode?.playables.find(item => item.id === assetId);

        return {
          ...state,

          rights,
          viewableMetadata: {
            title: viewable.title || '',
            banner: episode?.banner,
            start: isLiveEvent ? undefined : playable.startTimeUtc || 0,
            stop: isLiveEvent ? undefined : playable.stopTimeUtc || episode?.duration || playable?.duration || 0,
            isLiveEvent,
            contentRating: getRating(episode),

            episode: {
              title: episode?.title,
              seasonNumber: episode?.seasonNumber,
              episodeNumber: episode?.episodeNumber,
            },
          },
        };
      }

      const duration = viewable.duration || viewable.defaultPlayable?.duration || 0;
      const start = isLiveEvent ? undefined : (viewable.defaultPlayable?.startTimeUtc || 0);
      const stop = isLiveEvent ? undefined : (viewable.defaultPlayable?.stopTimeUtc || duration);

      return {
        ...state,

        rights,
        viewableMetadata: {
          title: viewable.title || '',
          banner: viewable.banner,
          start,
          stop,
          isLiveEvent,
          contentRating: getRating(viewable),
        },
      };
    }

    case playerEventsActions.PLAYING: {
      const { player } = action;

      return {
        ...state,

        playing: true,
        isInitialized: true,
        volume: player.model.volume,
        error: null,
      };
    }

    case playerEventsActions.PAUSE: {
      const { player } = action;

      return {
        ...state,

        playing: false,

        // update isBuffering state to avoid seing Spinner for a second on Safari
        isBuffering: player.model.isBuffering,
      };
    }

    case playerEventsActions.STREAM_ENDED: {
      return {
        ...state,

        buffered: NO_BUFFER,
        seekRange: NO_SEEK_RANGE,
        currentTime: 0,
        playing: false,
      };
    }

    case playerEventsActions.DESTROY: {
      const {
        chromecastState,
        chromecastFriendlyName,
        chromecastEnabled,
        isAirPlayAvailable,
        airplayEnabled,
        isPinCodeRequired,
        error,
        volume,
      } = state;
      return {
        ...defaultState,

        chromecastState,
        chromecastFriendlyName,
        chromecastEnabled,
        isAirPlayAvailable,
        airplayEnabled,
        isPinCodeRequired,
        error,
        volume,
      };
    }

    case playerEventsActions.FULLSCREEN: {
      const { player } = action;

      return {
        ...state,

        isFullscreen: player.model.isFullscreen,
      };
    }

    case playerEventsActions.INITIALIZED: {
      const { player } = action;

      return {
        ...state,

        isLive: player.model.isLive,
        streamDuration: player.model.streamDuration,
      };
    }

    case playerEventsActions.OFFSET_FROM_LIVE: {
      const { player } = action;

      return {
        ...state,

        offsetFromLive: player.model.offsetFromLive,
      };
    }

    case playerEventsActions.TIMEUPDATE: {
      const {
        volume,
        isLive,
        seekRange,
        currentTime,
        selectedAudio,
        selectedText,
        selectedBitrate,
        audioTracks,
        textTracks,
        videoTracks,
        buffer,
      } = action.player.model;

      return {
        ...state,

        currentTime,
        buffered: buffer,
        seekRange,

        audioTracks: normalizeOptions(audioTracks),
        textTracks: normalizeTextTracksOptions(textTracks.map(textTrack => textTrack.language)),
        videoTracks: normalizeOptions(videoTracks),

        audioTrack: selectedAudio,
        subtitleTrack: selectedText,
        bitrate: selectedBitrate,

        volume,
        isLive,
      };
    }

    case playerEventsActions.CHROMECAST_TIMEUPDATE: {
      const {
        player: {
          model: {
            currentTime,
            selectedQuality,
            volume,
            isPlaying,
          },
        },
        event: {
          detail: {
            streamDuration,
            friendlyName,
            isLive,

            audioTracks,
            textTracks,
            videoTracks,
            selectedAudio,
            selectedText,
          },
        },
      } = action;

      return {
        ...state,

        currentTime,
        isLive,
        isInitialized: true,

        audioTracks: normalizeOptions(audioTracks),
        textTracks: normalizeTextTracksOptions(textTracks),
        videoTracks: normalizeOptions(videoTracks),

        audioTrack: selectedAudio,
        subtitleTrack: selectedText,
        bitrate: selectedQuality,

        volume,
        chromecastFriendlyName: friendlyName || DEFAULT_DEVICE_NAME,
        chromecastState: CHROMECAST_STATES.CONNECTED,

        streamDuration,
        playing: isPlaying,
      };
    }

    case playerEventsActions.BUFFERING: {
      const { player } = action;

      return {
        ...state,

        isBuffering: player.model.isBuffering,
      };
    }

    case playerEventsActions.VOLUMECHANGE: {
      const { player } = action;

      return {
        ...state,

        volume: player.model.volume,
      };
    }

    case playerEventsActions.CHROMECAST_AVAILABLE:
    case playerEventsActions.CHROMECAST_ACTIVATED:
    case playerEventsActions.CHROMECAST_DEACTIVATED: {
      const { player } = action;

      return {
        ...state,

        chromecastState: getChromecastState(player),
      };
    }

    case playerEventsActions.AIRPLAY_AVAILABLE: {
      const { player } = action;

      return {
        ...state,

        isAirPlayAvailable: player.model.isAirPlayAvailable,
      };
    }

    case playerEventsActions.AIRPLAYING: {
      const { player } = action;
      return {
        ...state,
        isAirPlaying: player.model.isAirPlaying,
      };
    }

    case playerEventsActions.ADS_TIMEUPDATE: {
      const {
        currentTime,
        secondsLeftToSkip,
      } = action.event.detail.playerInfo;

      return {
        ...state,

        currentTimeAd: currentTime,
        adSkipCountdown: secondsLeftToSkip,
      };
    }

    case playerEventsActions.ADS_ACTIVATED: {
      return {
        ...state,

        isPlayingAds: true,
      };
    }

    case playerEventsActions.ADS_PLAYING: {
      const {
        playerInfo,
        adTitle,
        adsCount,
        adsLeft,
        adHasLink,
      } = action.event.detail;

      return {
        ...state,

        isPlayingAds: true,
        playing: true,
        adsMetadata: {
          start: playerInfo.seekRange.start,
          stop: playerInfo.seekRange.end,
        },
        adTitle,
        adsCount,
        adsLeft,
        adHasLink,
      };
    }

    case playerEventsActions.ADS_PAUSE: {
      return {
        ...state,

        playing: false,
      };
    }

    case playerEventsActions.ADS_ENDED:
    case playerEventsActions.ADS_DEACTIVATED: {
      return {
        ...state,

        ...action.type === playerEventsActions.ADS_DEACTIVATED
          ? {
            isPlayingAds: false,
            adsCount: 0,
            adsLeft: 0,
          } : {},
        playing: false,
        adsMetadata: null,
        adTitle: null,
        adHasLink: false,
        adSkipCountdown: -1,
      };
    }

    case playerEventsActions.DAI_ADS_PLAYING: {
      return {
        ...state,
        isPlayingDaiAds: true,
      };
    }

    case playerEventsActions.DAI_ADS_ENDED: {
      return {
        ...state,
        isPlayingDaiAds: false,
      };
    }

    case playerEventsActions.ENTITLEMENT: {
      return {
        ...state,

        isPinCodeRequired: false,
        error: null,
      };
    }

    case playerEventsActions.PINCODE_INCORRECT:
    case playerEventsActions.PINCODE_REQUIRED: {
      return {
        ...state,

        isPinCodeRequired: true,
      };
    }

    case playerEventsActions.ERROR: {
      const { player } = action;

      return {
        ...state,

        error: player.model.playerError,
      };
    }

    case playerEventsActions.SET_CURRENT_TIME: {
      const { currentTime } = action.detail;
      return {
        ...state,
        currentTime,
      };
    }

    case playerEventsActions.CHANGEAUDIOTRACK: {
      const { audioTrack } = action;

      return {
        ...state,

        audioTrack,
      };
    }

    case playerEventsActions.CHANGETEXTTRACK: {
      const { subtitleTrack } = action;

      return {
        ...state,

        subtitleTrack,
      };
    }

    case playerEventsActions.CHANGEBITRATE: {
      const { bitrate } = action;

      return {
        ...state,

        bitrate,
      };
    }

    default: {
      return state;
    }
  }
}
